'use client';
import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import RenderButtons from '@src/components/Shared/RenderButtons';
import Container from '@src/components/Shared/Container';
import SectionHeader from '@src/components/Shared/SectionHeader';
import CenteredCarousel from '@src/components/Shared/CenteredCarousel';
import InlineSVG from '@src/components/Shared/InlineSVG';
import NextImage from '@src/components/Shared/NextImage';
import NextLink from '@src/components/Shared/NextLink';

import HeroValdProd from '@src/components/Sections/HeroValdProd';
import ImageTextSecondaryWIthSlideAnimation from '@src/components/Sections/ImageTextSecondaryWIthSlideAnimation';
import ImageTextSecondary from '@src/components/Sections/ImageTextSecondary';
import ForceDecksBlogsCarousel from '@src/components/Sections/ForceDecksBlogsCarousel';
import FeaturedProducts from '@src/components/Sections/FeaturedProducts';
import CallToAction from '@src/components/Sections/CallToAction';

import usePreviewContent from '@src/hooks/usePreviewContent';

import constructURL from '@src/utils/constructURL';

import { ForceDecksPageClientPropTypes } from './ForceDecksPage.types';
import SubscriptionTable from '@src/components/Shared/SubscriptionTable';
import Testimonials from '@src/components/Sections/Testimonials';

const ForceDecksPageClient: React.FC<ForceDecksPageClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const page = usePreviewContent({ content: data?.page });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(
    constructURL({ languageCode: page?.languageCode?.toLowerCase(), slug: `/products/forcedecks`, baseURL: '' }),
  );
  setContentType('site-page');

  return (
    <>
      <HeroValdProd
        id={page?.hero?.items?.[0]?.scrollTo?.slice(1)}
        ctfData={page?.hero?.items?.[0]}
        className='mt-[4rem]! mb-[0rem]! lg:mb-[6.5rem]! 2xl:mt-[-2rem]!'
        imgClassName='mt-[2rem] md:absolute md:right-[0] md:w-[55rem] md:bottom-[2rem] lg:relative lg:w-[60rem] lg:bottom-[-2rem] 2xl:left-[5rem] 2xl:w-7xl 3xl:left-[-10rem] xsm:min-h-[20.5797rem] md:min-h-[30rem] lg:min-h-[33rem] 2xl:min-h-[35.3rem] 3xl:min-h-[38.2484rem]'
        logoClassName='mt-[3rem] md:mt-[5.7rem] max-w-[27.6386rem] mb-[2.4rem] md:max-w-[25.7rem] md:mt-[4.1rem] lg:max-w-[35.9302rem] 2xl:max-w-[53.96rem] xsm:min-h-[5.3219rem] md:min-h-[4.9484rem] lg:min-h-[6.9188rem] 2xl:min-h-[10.3906rem]'
        introClassName='[&>p]:tracking-[-0.0342rem] [&>p]:leading-[2.7rem] [&>p]:text-[1.8rem] [&>p]:max-w-[30.5rem] md:[&>p]:text-[2rem] md:[&>p]:leading-[3rem] md:[&>p]:tracking-[-0.036rem] md:[&>p]:max-w-[39.6rem] lg:[&>p]:max-w-[48.5rem] lg:[&>p]:text-[2.4rem] lg:[&>p]:leading-[3.6rem] 2xl:[&>p]:max-w-[58.3rem] [&_a]:text-[1.8rem] md:[&_a]:text-[2rem] lg:[&_a]:text-[2.4rem]'
        headerClassName='md:mx-[unset] md:w-[80%] md:self-start lg:self-center lg:w-[50%]'
      />

      <ImageTextSecondaryWIthSlideAnimation
        id={page?.easeOfUse?.items?.[0]?.scrollTo?.slice(1)}
        className='3xl:py-[8rem]! py-[5rem] lg:py-[0] xl:overflow-visible! 2xl:py-[3rem]! [&>div>header>h2>span]:absolute [&>div>header>h2>span]:top-[-0.2rem] [&>div>header>h2>span]:ml-[0.8rem] [&>div>header>h2>span]:leading-[2.3rem] [&>div>header>h2>span]:opacity-[0] lg:[&>div>header>h2>span]:top-[unset] lg:[&>div>header>h2>span]:ml-[1.3rem] lg:[&>div>header>h2>span]:leading-[unset]'
        background='bg-background-primary'
        reversed
        slide
        pretitle={page?.easeOfUse?.items?.[0]?.preTitle}
        title={page?.easeOfUse?.items?.[0]?.title}
        titleClassName='text-body-copy--light text-[2.6rem]! lg:text-[4.8rem]!'
        imgURL={page?.easeOfUse?.items?.[0]?.mediaPrimary?.url}
        imgAlt={page?.easeOfUse?.items?.[0]?.mediaPrimary?.description}
        imgSecondClassName='xl:absolute! xl:bottom-[0] xl:h-[118%] xl:right-[0]'
        textContainerClassName='xl:w-[45%]!'
        objectFit='contain'
        childrenElements={
          <div className='text-body-copy--light'>
            <RenderBodyCopy
              className='max-w-[51.7rem] text-left 2xl:max-w-[78.2rem]'
              bodyCopy={page?.easeOfUse?.items?.[0]?.bodyCopy?.json?.content}
            />

            <RenderButtons
              className='[&>div>a[data-pseudo]]:text-primary!'
              items={page?.easeOfUse?.items?.[0]?.buttonCollection?.items}
            />
          </div>
        }
      />

      <ImageTextSecondary
        className='lg:py-[0]'
        background='lg:ml-[unset]! lg:mr-[unset]! lg:w-full! '
        id={page?.usability?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.usability?.items?.[0]?.preTitle}
        title={page?.usability?.items?.[0]?.title}
        imgURL={page?.usability?.items?.[0]?.mediaPrimary?.url}
        titleClassName='md:max-w-[40rem]'
        imgAlt={page?.usability?.items?.[0]?.mediaPrimary?.description}
        objectFit='contain'
        imgSecondClassName='lg:w-[70%]!'
        childrenElements={
          <>
            <RenderBodyCopy
              className='max-w-[51.7rem] text-left 2xl:max-w-[78.2rem]'
              bodyCopy={page?.usability?.items?.[0]?.bodyCopy?.json?.content}
            />

            <RenderButtons className='w-full' items={page?.usability?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <Container
        id={page?.versatility?.items?.[0]?.scrollTo?.slice(1)}
        className='py-[7.4rem] md:flex md:flex-col'
        background='bg-background-tertiary'
      >
        <SectionHeader
          className='justify-center lg:pb-[unset]'
          pretitle={page?.versatility?.items?.[0]?.preTitle}
          title={page?.versatility?.items?.[0]?.title}
          titleClassName='max-w-[71.6rem] text-[2.8rem] mb-[2.4rem] leading-[3.2rem] md:leading-[4.6rem] lg:text-[4.8rem] lg:leading-[5rem]'
          align='center'
        >
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.versatility?.items?.[0]?.bodyCopy?.json?.content}
            />

            <RenderButtons
              className='mb-[6rem] w-full justify-center'
              items={page?.versatility?.items?.[0]?.buttonCollection?.items}
            />
          </>
        </SectionHeader>

        {/*
          These assets are 408x408 PNGs holding a 360x360 photo at (24, 12) — transparent margin, with a
          shadow baked into it. Left alone the photo lands 12% smaller than on every other carousel and
          its title sits ~19px lower, so the padding is scaled back out here (408/360, offset by the
          inset) and the card's own frame is clipped and shadowed by the component. Re-exporting the
          assets flush to their edges is the durable fix; this crop can go when that happens.
        */}
        <CenteredCarousel
          list={page?.versatility?.items?.[0]?.extraAssetsCollection?.items}
          imgClassName='origin-top-left -translate-x-[6.6667%] -translate-y-[3.3333%] scale-[1.13333]'
        />
      </Container>

      <ImageTextSecondary
        id={page?.togetherAtLast?.items?.[0]?.scrollTo?.slice(1)}
        background='lg:ml-[unset]! lg:mr-[unset]! lg:w-full!'
        pretitle={page?.togetherAtLast?.items?.[0]?.preTitle}
        title={page?.togetherAtLast?.items?.[0]?.title}
        titleClassName='md:max-w-[46rem]'
        imgURL={page?.togetherAtLast?.items?.[0]?.mediaPrimary?.url}
        imgAlt={page?.togetherAtLast?.items?.[0]?.mediaPrimary?.description}
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.togetherAtLast?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.togetherAtLast?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <ImageTextSecondary
        reversed
        id={page?.introducingNorms?.items?.[0]?.scrollTo?.slice(1)}
        className='py-[5rem] 2xl:overflow-visible!'
        background='bg-background-primary'
        pretitle={page?.introducingNorms?.items?.[0]?.preTitle}
        title={page?.introducingNorms?.items?.[0]?.title}
        titleClassName='md:max-w-[60rem] text-body-copy--light'
        imgURL={page?.introducingNorms?.items?.[0]?.mediaPrimary?.url}
        imgAlt={page?.introducingNorms?.items?.[0]?.mediaPrimary?.description}
        objectFit='contain'
        textContainerClassName='text-body-copy--light'
        childrenElements={
          <>
            <RenderBodyCopy
              className='t2xl:max-w-[78.2rem]'
              bodyCopy={page?.introducingNorms?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons
              className='[&>div>a[data-pseudo]]:text-primary!'
              items={page?.introducingNorms?.items?.[0]?.buttonCollection?.items}
            />
          </>
        }
      />

      <ImageTextSecondary
        id={page?.centreOfPressure?.items?.[0]?.scrollTo?.slice(1)}
        background='lg:ml-[unset]! lg:mr-[unset]! lg:w-full!'
        pretitle={page?.centreOfPressure?.items?.[0]?.preTitle}
        title={page?.centreOfPressure?.items?.[0]?.title}
        titleClassName='md:max-w-[46rem]'
        imgURL={page?.centreOfPressure?.items?.[0]?.mediaPrimary?.url}
        imgAlt={page?.centreOfPressure?.items?.[0]?.mediaPrimary?.description}
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.centreOfPressure?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.centreOfPressure?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <Container
        id={page?.fitForPurpose?.items?.[0]?.scrollTo?.slice(1)}
        className='mb-[0rem]! py-[7.4rem] md:flex md:flex-col'
        background='bg-background-tertiary'
      >
        <SectionHeader
          className='justify-center md:mb-[3.4rem] lg:pb-[unset]'
          pretitle={page?.fitForPurpose?.items?.[0]?.preTitle}
          title={page?.fitForPurpose?.items?.[0]?.title}
          titleClassName='max-w-[71.6rem] text-[2.8rem] mb-[2.4rem] leading-[3.2rem] md:leading-[4.6rem] lg:text-[4.8rem] lg:leading-[5rem]'
          align='center'
        >
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.fitForPurpose?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons
              className='justify-center'
              items={page?.fitForPurpose?.items?.[0]?.buttonCollection?.items}
            />
          </>
        </SectionHeader>

        <InlineSVG
          className='mt-[4rem] md:hidden'
          src={page?.fitForPurpose?.items?.[0]?.mediaTertiary?.url}
          renderAt={['mobile']}
        />

        <InlineSVG
          className='mb-[4rem] hidden md:block lg:hidden'
          src={page?.fitForPurpose?.items?.[0]?.mediaSecondary?.url}
          renderAt={['tablet']}
        />

        <InlineSVG
          className='hidden lg:block'
          src={page?.fitForPurpose?.items?.[0]?.mediaPrimary?.url}
          renderAt={['desktop']}
        />

        <ImageTextSecondary
          reversed
          className='left-[0] mt-[4.2rem] mb-[0]! w-full justify-around gap-[0]! overflow-visible! px-[0]! py-[0] 2xl:left-[15%] 2xl:mt-[-8rem] 2xl:w-[75%]'
          title={page?.fitForPurpose?.items?.[0]?.extraAssetsCollection?.items?.[0]?.title}
          titleClassName='text-[1.6rem] whitespace-nowrap	 lg:text-[2.8rem] 2xl:text-[3.6rem] mb-[2.4rem] leading-[2rem] lg:leading-[3rem] 2xl:text-right 2xl:leading-[4rem] font-light'
          imgURL={page?.fitForPurpose?.items?.[0]?.extraAssetsCollection?.items?.[0]?.mediaPrimary?.url}
          imgAlt={page?.fitForPurpose?.items?.[0]?.mediaSecondary?.description}
          imgFirstClassName='hidden lg:block lg:w-[10rem]!'
          imgSecondClassName='lg:max-w-xs lg:w-[30%]! lg:right-[-10rem]'
          objectFit='contain'
          textContainerClassName='2xl:relative 2xl:z-10 lg:w-[60%]!'
          childrenElements={
            <>
              <div className='md:flex'>
                <div className='mb-[3.2rem] md:w-3xl lg:w-full'>
                  <RenderBodyCopy
                    className='text-left 2xl:text-right'
                    bodyCopy={
                      page?.fitForPurpose?.items?.[0]?.extraAssetsCollection?.items?.[0]?.bodyCopy?.json?.content
                    }
                  />

                  <div className='hidden items-center justify-start md:block lg:justify-end!'>
                    <RenderButtons
                      className='mx-auto mt-[2.4rem]! 2xl:justify-end'
                      items={
                        page?.fitForPurpose?.items?.[0]?.extraAssetsCollection?.items?.[0]?.buttonCollection?.items
                      }
                    />
                  </div>
                </div>

                <div>
                  <NextImage
                    className='mx-auto w-[21rem] md:relative md:top-[-13rem] md:mb-[-13rem] md:max-w-[unset] lg:hidden'
                    src={page?.fitForPurpose?.items?.[0]?.extraAssetsCollection?.items?.[0]?.mediaSecondary?.url}
                    alt=''
                    aspectRatio={
                      page?.fitForPurpose?.items?.[0]?.extraAssetsCollection?.items?.[0]?.mediaSecondary?.width /
                      page?.fitForPurpose?.items?.[0]?.extraAssetsCollection?.items?.[0]?.mediaSecondary?.height
                    }
                    width={page?.fitForPurpose?.items?.[0]?.extraAssetsCollection?.items?.[0]?.mediaSecondary?.width}
                  ></NextImage>

                  <div className='flex items-center justify-start md:hidden'>
                    <RenderButtons
                      className='mx-auto mt-[2.4rem]!'
                      items={
                        page?.fitForPurpose?.items?.[0]?.extraAssetsCollection?.items?.[0]?.buttonCollection?.items
                      }
                    />
                  </div>
                </div>
              </div>
            </>
          }
        />
      </Container>

      <ImageTextSecondary
        className='lg:mb-[0rem]! xl:overflow-visible!'
        imgSecondClassName='[&>img]:w-auto! [&>img]:max-w-[unset] [&>img]:h-[112%]! lg:flex! justify-end overflow-visible! h-[43.8rem]'
        id={page?.fullyValidate?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.fullyValidate?.items?.[0]?.preTitle}
        title={page?.fullyValidate?.items?.[0]?.title}
        imgURL={page?.fullyValidate?.items?.[0]?.mediaPrimary?.url}
        imgAlt={page?.fullyValidate?.items?.[0]?.mediaPrimary?.description}
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy
              className='lg:max-w-[58rem]'
              bodyCopy={page?.fullyValidate?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.fullyValidate?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <Container className='mb-[0rem]! py-[8rem]! lg:mb-[0rem]! lg:py-[8rem]' background='bg-background-tertiary'>
        <ImageTextSecondary
          reversed
          className='mb-[0rem]! px-[0rem]!'
          id={page?.accessories?.items?.[0]?.scrollTo?.slice(1)}
          pretitle={page?.accessories?.items?.[0]?.preTitle}
          title={page?.accessories?.items?.[0]?.title}
          imgURL={page?.accessories?.items?.[0]?.mediaPrimary?.url}
          imgAlt={page?.accessories?.items?.[0]?.mediaPrimary?.description}
          objectFit='cover'
          childrenElements={
            <>
              <RenderBodyCopy
                className='2xl:max-w-[78.2rem]'
                bodyCopy={page?.accessories?.items?.[0]?.bodyCopy?.json?.content}
              />
              <RenderButtons items={page?.accessories?.items?.[0]?.buttonCollection?.items} />
            </>
          }
        />
      </Container>

      <Container background='bg-primary' className='mb-[0]! py-[2rem] md:flex md:items-center md:gap-[3.2rem]'>
        {/* Mobile clickable banner */}
        <NextLink
          href='/news/introducing-valdr'
          className='relative flex items-center font-light md:hidden md:gap-[3.2rem]'
        >
          <RenderBodyCopy
            className='text-body-copy--light locale-es:max-w-[26rem] locale-fr:max-w-[29rem] inline-block max-w-sm md:max-w-[unset]'
            bodyCopy={page?.accessories?.items?.[0]?.extraAssetsCollection?.items?.[0]?.bodyCopy?.json?.content}
          />
          <RenderButtons
            className='[&>div>a]:text-body-copy--light! hover:[&>div>a]:text-body-copy--light! m-[0]! hidden! md:block! [&>div]:w-[12rem] md:[&>div]:w-[unset] [&>div>a]:border-[#fff]! [&>div>a]:before:content-[""]!'
            items={page?.accessories?.items?.[0]?.extraAssetsCollection?.items?.[0]?.buttonCollection?.items}
          />
          <svg
            className='locale-fr:left-[-5.5rem] relative bottom-[-1.2rem] left-[-2.1rem] inline-block w-[1rem] md:hidden'
            width='6'
            height='11'
            viewBox='0 0 6 11'
            fill='fff'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M5.87946 5.19591C6.04018 5.35457 6.04018 5.64543 5.87946 5.80409L0.736607 10.881C0.575893 11.0397 0.28125 11.0397 0.120536 10.881C-0.0401786 10.7224 -0.0401786 10.4315 0.120536 10.2728L4.96875 5.51322L0.120536 0.727163C-0.0401781 0.56851 -0.0401781 0.277644 0.120536 0.11899C0.28125 -0.0396635 0.575893 -0.0396635 0.736608 0.11899L5.87946 5.19591Z'
              fill='white'
            />
          </svg>
        </NextLink>

        {/* Desktop */}
        <RenderBodyCopy
          className='text-body-copy--light hidden max-w-sm md:inline-block md:max-w-[unset]'
          bodyCopy={page?.accessories?.items?.[0]?.extraAssetsCollection?.items?.[0]?.bodyCopy?.json?.content}
        />
        <RenderButtons
          className='[&>div>a]:text-body-copy--light! hover:[&>div>a]:text-body-copy--light! m-[0]! hidden! md:block! [&>div]:w-[12rem] md:[&>div]:w-[unset] [&>div>a]:border-[#fff]! [&>div>a]:before:content-[""]!'
          items={page?.accessories?.items?.[0]?.extraAssetsCollection?.items?.[0]?.buttonCollection?.items}
        />
      </Container>

      <ForceDecksBlogsCarousel className='mb-[0rem]!' data={page?.carouselPosts?.items?.[0]} />

      <Testimonials
        id={page?.testimonials?.items?.[0]?.scrollTo?.slice(1)}
        testimonials={page?.testimonials?.items?.[0]?.topicCollection?.items}
        bgWhite={true}
      />

      <Container
        id={page?.valdSubscription?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-[#F9F9F9]'
        className='mt-[0]! py-[8rem]'
      >
        <SectionHeader
          className='mb-[2.4rem] lg:mb-[unset]'
          childrenClassName='w-full'
          pretitle={page?.valdSubscription?.items?.[0]?.preTitle}
          title={page?.valdSubscription?.items?.[0]?.title}
          titleClassName='text-[2.8rem] xl:text-[4.8rem] [&>span]:inline-block mb-[2.4rem] leading-[3.2rem] md:leading-[4.6rem] xl:leading-[5rem] font-light xl:max-w-[60rem] inline-block'
          align='center'
        >
          <RenderButtons
            items={page?.valdSubscription?.items?.[0]?.buttonCollection?.items}
            className='mt-[1rem]! justify-center'
          />
        </SectionHeader>
        <SubscriptionTable
          headerImgL='w-[13rem] lg:w-[16.5rem]'
          headerImgR='w-[9.5rem] lg:w-[11.2rem]'
          data={page?.valdSubscription?.items}
        />
      </Container>

      <FeaturedProducts data={page?.valdSystems?.items?.[0]} />

      <CallToAction
        id={page?.callToAction?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.callToAction?.items?.[0]?.title}
        bodyCopy={page?.callToAction?.items?.[0]?.bodyCopy?.json?.content}
        buttons={page?.callToAction?.items?.[0]?.buttonCollection?.items}
      />
    </>
  );
};

export default ForceDecksPageClient;
